import { Data } from '.generated/templates/Feature.Data.model';
import { Field } from '@sitecore-jss/sitecore-jss-react';
import saveAs from 'file-saver';
import { DateTime } from 'luxon';
import { logError, makeLoggableError } from './errors';
import { formatDateShortMonth } from './date-formatters';

function formatMeridiem(time: string): string {
  return time.replace(/\bAM\b/g, 'a.m.').replace(/\bPM\b/g, 'p.m.');
}

function omitZeroMinutes(dt: DateTime, withZone = false): string {
  const hasMinutes = dt.minute !== 0;
  const format = hasMinutes ? `h:mm a${withZone ? ' ZZZZ' : ''}` : `h a${withZone ? ' ZZZZ' : ''}`;
  return formatMeridiem(dt.toFormat(format));
}

export const undefinedDate = '0001-01-01T00:00:00Z';

export const getFormattedEventDate = (eventStartDate: string, eventEndDate: string): string => {
  if (!eventStartDate || eventStartDate === undefinedDate) return '';

  const eventStartDateTime = DateTime.fromISO(eventStartDate);
  if (!eventStartDateTime.isValid) return '';

  let formattedEventDate = formatDateShortMonth(eventStartDate);

  if (eventEndDate && eventEndDate !== undefinedDate) {
    const eventEndDateTime = DateTime.fromISO(eventEndDate);
    if (eventEndDateTime.isValid && eventStartDateTime.day !== eventEndDateTime.day) {
      formattedEventDate = `${formattedEventDate} - ${formatDateShortMonth(eventEndDate)}`;
    }
  }

  return formattedEventDate;
};

export const getFormattedStartTime = (eventStartDate: string, eventEndDate: string): string => {
  if (eventStartDate !== undefinedDate) {
    const eventStartDateTime = DateTime.fromISO(eventStartDate);
    const withZone = eventEndDate === undefinedDate;
    return omitZeroMinutes(eventStartDateTime, withZone);
  }
  return '';
};

export const getFormattedEndTime = (eventEndDate: string): string => {
  if (eventEndDate !== undefinedDate) {
    const eventEndDateTime = DateTime.fromISO(eventEndDate);
    return ` - ${omitZeroMinutes(eventEndDateTime, true)}`;
  }
  return '';
};

export const addToCalendar = async (
  pageTitle: string,
  pageDescription: string,
  eventStartDate: string,
  eventEndDate: string,
  location: string,
  eventCategory: Data.Events.Fields.EventCategory[]
): Promise<void> => {
  const eventStartDateTime = DateTime.fromISO(eventStartDate);
  const eventEndDateTime = DateTime.fromISO(eventEndDate);
  const eventStartYear = eventStartDateTime.year;
  const eventStartMonth = eventStartDateTime.month;
  const eventStartDay = eventStartDateTime.day;
  const eventStartHour = eventStartDateTime.hour;
  const eventStartMinute = eventStartDateTime.minute;
  const eventEndYear = eventEndDateTime.year;
  const eventEndMonth = eventEndDateTime.month;
  const eventEndDay = eventEndDateTime.day;
  const eventEndHour = eventEndDateTime.hour;
  const eventEndMinute = eventEndDateTime.minute;
  const eventCategoriesArray: string[] = [];

  eventCategory.forEach(function BuildCategoriesString(value) {
    const eventCategoryTitle: Field<string> = value.fields?.eventCategoryTitle as Field<string>;
    eventCategoriesArray.push(eventCategoryTitle.value);
  });
  const createEvent = (await import('ics')).createEvent;
  createEvent(
    {
      title: pageTitle,
      description: pageDescription,
      busyStatus: 'FREE',
      start: [eventStartYear, eventStartMonth, eventStartDay, eventStartHour, eventStartMinute],
      end: [eventEndYear, eventEndMonth, eventEndDay, eventEndHour, eventEndMinute],
      location: location,
      categories: eventCategoriesArray,
    },
    (error, value) => {
      if (error) {
        logError('addToCalendar', makeLoggableError({ error: error }));
      }
      const file = new File([value], 'event.ics', {
        type: 'text/calendar;charset=utf-8',
      });
      saveAs(file);
    }
  );
};
